import React, { useEffect, useState, useRef, useMemo } from "react";
import { Annonce } from "../../models/AnnonceModel";
import { FaFilePdf, FaInfoCircle, FaGift } from "react-icons/fa";
import logo from "../../image/logo_alo.png";
import { getPointsByPaymentRef } from "../../services/Payment_service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../page/style/style.css";

interface DetailAnnonceProps {
  annonceNow: Annonce;
  closeModal: () => void;
}

const API_URL = process.env.REACT_APP_API_URL;

const AnnonceDetail: React.FC<DetailAnnonceProps> = ({ annonceNow }) => {
  const [pointsTemp, setPointsTemp] = useState<number | null>(null);
  const [loadingPoints, setLoadingPoints] = useState<boolean>(false);

  const hasShownToast = useRef<string | null>(null);
  const isRequestInProgress = useRef<boolean>(false);

  const paymentRef = useMemo(() => annonceNow?.paymentRef, [annonceNow?.paymentRef]);

  useEffect(() => {
    if (paymentRef &&
      hasShownToast.current !== paymentRef &&
      !isRequestInProgress.current) {

      isRequestInProgress.current = true;
      setLoadingPoints(true);

      getPointsByPaymentRef(paymentRef)
        .then((data) => {
          if (data.success) {
            setPointsTemp(data.pointsEarned ?? 0);
            hasShownToast.current = paymentRef;
          } else {
            setPointsTemp(0);
          }
        })
        .catch((err) => {
          console.error("Erreur récupération points:", err);
          setPointsTemp(0);
          hasShownToast.current = paymentRef;

          toast.error("Erreur lors de la récupération des points !", {
            toastId: `error-${paymentRef}`,
            position: "top-right",
            autoClose: 5000,
          });
        })
        .finally(() => {
          setLoadingPoints(false);
          isRequestInProgress.current = false;
        });
    }
  }, [paymentRef]);

  useEffect(() => {
    return () => {
      hasShownToast.current = null;
      isRequestInProgress.current = false;
    };
  }, []);

  const formatDateParrution = (dateParrution?: string | Date | null): string => {
    if (!dateParrution) return "Date non disponible";
    const dateObj = dateParrution instanceof Date ? dateParrution : new Date(dateParrution);
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return `le ${dateObj.toLocaleDateString("fr-FR", dateOptions)}`;
  };

  const formatPrice = (price: any): string => {
    const num = Number(price);
    return !isNaN(num) ? num.toFixed(2) : "0.00";
  };

  const fileUrlAttestationLegale =
    API_URL && annonceNow.pdfAttestation
      ? `${API_URL}/download-attestation/${annonceNow.pdfAttestation.split("/")?.[2]}`
      : "#";

  const fileUrlAttestationAlo =
    API_URL && annonceNow.pdfAttestationAlo
      ? `${API_URL}/download-attestation/${annonceNow.pdfAttestationAlo.split("/")?.[2]}`
      : "#";

  const fileUrlFacture =
    API_URL && annonceNow.pdfFactureAlo
      ? `${API_URL}/download-factures/${annonceNow.pdfFactureAlo.split("/")?.[2]}`
      : "#";

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ background: "#fff", marginTop: "0px", gap: "20px", padding: "20px 20px 10px 20px", maxWidth: "100%" }}
      >
        <div
          style={{
            backgroundColor: "#2A919D",
            borderRadius: "10px",
            padding: "12px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "150px",
          }}
        >
          <img src={logo} alt="logo" style={{ height: "40px" }} />
        </div>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 20px",
            boxShadow: "0 4px 5px rgba(0, 0, 0, 0.15)",
            fontSize: "1.5rem",
          }}
        >
          Récapitulatif de l'annonce
        </div>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "12px 20px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
            fontWeight: "bold",
            fontSize: "1.2rem",
            color: "#2A919D",
            minWidth: "200px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <FaGift size={20} color="#2A919D" />
          {loadingPoints ? "Chargement des points..." : `Points gagnés : ${pointsTemp ?? 0}`}
        </div>
      </div>
      <div className="announcement-detail-content" style={{ background: "#fff", padding: "10px", width: "100%" }}>
        <div className="row">
          <div className="col-6">
            <div className="dossier-info">
              <p>
                <strong>
                  Dossier n° {annonceNow.numDossier} - Annonce n° {annonceNow.numAnnonce}
                </strong>
              </p>
              <textarea
                className="textarea"
                value={annonceNow.textAnnonce ?? ""}
                placeholder="Entrez vos informations ici..."
                readOnly
              />
            </div>
          </div>

          <div className="col-6">
            <section className="support-info">
              <h5>Détail de l'annonce</h5>

              <div className="container px-0">
                <div className="row mb-2">
                  <div className="col-4 text-start fw-bold">Support :</div>
                  <div className="col-8 text-start">{annonceNow.nomJal ?? "-"}</div>
                </div>

                <div className="row mb-2">
                  <div className="col-4 text-start fw-bold">Parution :</div>
                  <div className="col-8 text-start">
                    {formatDateParrution(annonceNow.dateParrution)}
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-4 text-start fw-bold">Justificatif :</div>
                  <div className="col-8 text-start">
                    {annonceNow.pdf_justif_lien_public && annonceNow.pdf_justif_state === 1 ? (
                      <span className="badge" style={{ color:"white", background:"#4BB0C0" }}>Oui</span>
                    ) : (
                      <span className="badge" style={{ color:"white", background:"#eb8635" }}>En attente</span>
                    )}
                  </div>
                </div>
              </div>


              <section className="devis-section my-3">
                <a href={annonceNow.isAttestationFromAlo ? fileUrlAttestationAlo : fileUrlAttestationLegale} download target="_blank" rel="noopener noreferrer">
                  <button className="devis-download-button my-2">
                    <FaFilePdf className="pdf-icon"
                      style={{ maxHeight: "30px", maxWidth: "30px" }} />
                    Télécharger l'attestation {annonceNow.isAttestationFromAlo ? "ALO" : "LEGALE"}
                  </button>
                </a>

                {/* Afficher le bouton de facture uniquement si l'annonce est payée */}
                {annonceNow.isPaid && (
                  <a href={fileUrlFacture} download target="_blank" rel="noopener noreferrer">
                    <button className="devis-download-button my-2">
                      <FaFilePdf className="pdf-icon"
                        style={{ maxHeight: "30px", maxWidth: "30px" }} />
                      Télécharger la facture
                    </button>
                  </a>
                )}

                {annonceNow.pdf_justif_lien_public && annonceNow.pdf_justif_state === 1 ? (
                  <a href={annonceNow.pdf_justif_lien_public} download target="_blank" rel="noopener noreferrer">
                    <button className="devis-download-button my-2">
                      <FaFilePdf className="pdf-icon"
                        style={{ maxHeight: "30px", maxWidth: "30px" }} />
                      Télécharger le PDF Justificatif
                    </button>
                  </a>
                ) : (
                  <div className="devis-download-button my-2">
                    <FaInfoCircle className="pdf-icon"
                      style={{ maxHeight: "30px", maxWidth: "30px" }} />
                    PDF Justificatif en attente
                  </div>
                )}
              </section>

              <section className="tarif-info mt-3 mx-3">
                <h6 className="tarif-title">Tarif de l'annonce</h6>
                <p className="prixht" style={{ height: "35px" }}>
                  {formatPrice(annonceNow.montantTTC)} €TTC
                </p>
              </section>
            </section>
          </div>

        </div>
      </div>
    </>
  );
};

export default AnnonceDetail;